<template>
  <div
    v-if="items.length"
    class="bg-gray-50 flex flex-col items-center py-20 px-0 gap-[100px] w-full"
  >
    <div class="flex flex-col items-center gap-12 w-full max-w-[917px]">
      <a
        href="#table-of-contents"
        class="flex items-center text-center text-3xl font-[450] leading-[125%] text-black"
        @click.prevent="scrollToElement('#table-of-contents')"
      >
        <span>{{ $texts('toc.title') }}</span>
      </a>
      <nav>
        <ul
          id="table-of-contents"
          class="flex flex-col px-4 sm:px-0 sm:flex-row sm:flex-wrap place-content-center items-start gap-x-3 gap-y-4 w-full"
        >
          <li v-for="(item, key) in items" :key="key">
            <a
              :href="item.link"
              class="button bg-lightGreen text-lg xl:text-xl text-balance font-regular items-center"
              @click.prevent="scrollToElement(item.link)"
            >
              <span>{{ item.title }}</span>
              <SpriteSymbol
                inline
                name="icon-arrow-down"
                class="size-4 sm:size-10 opacity-80 transition-all duration-300 ease-in-out shrink-0 ml-2 sm:ml-0"
              />
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { notNullish } from '@vueuse/core'
import { slugify } from '#vuepal/helpers/url'
import { useNuxtApp } from '#app'
import { getRuntimeOptions } from '#blokkli/runtime-helpers'

const { $texts } = useNuxtApp()

function scrollToElement(selector: string) {
  // Extract the ID from the selector (remove the # if present)
  const id = selector.startsWith('#') ? selector.substring(1) : selector

  // Prevent default browser jump by updating hash without triggering default behavior
  history.pushState(null, '', '#' + id)

  // Try to find the accordion element directly and open it
  const accordionElement = document.getElementById(id)
  if (accordionElement) {
    // Try to find the closest Collapsible component
    const detailsElement = accordionElement.closest('details')
    if (detailsElement) {
      // Force open the details element
      detailsElement.open = true
      detailsElement.classList.add('open')
    }
  }

  // Manually trigger the hashchange event
  try {
    // Create a complete HashChangeEvent with oldURL and newURL
    const oldURL = window.location.href.replace(window.location.hash, '')
    const newURL = window.location.href
    const hashChangeEvent = new HashChangeEvent('hashchange', {
      oldURL: oldURL,
      newURL: newURL,
    })
    window.dispatchEvent(hashChangeEvent)
  } catch {
    // Fallback to a simpler event if HashChangeEvent constructor fails
    const event = new Event('hashchange')
    window.dispatchEvent(event)
  }

  // Wait a longer time to allow the accordion to open
  setTimeout(() => {
    const element = document.querySelector('#' + id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }, 300) // Increased timeout to 500ms to give more time for the accordion to open
}

const { queryBlocks } = useBlokkliHelper()

const blocks = queryBlocks(['title', 'accordeon', 'info_box'])

const items = computed(() => {
  return blocks.value
    .map((item) => {
      const options = getRuntimeOptions(item)
      if (!options.showInToc) return null

      const title = item.props?.title
      if (!title) return null
      const slug = slugify(title)
      return {
        title,
        link: '#' + slug,
      }
    })
    .filter(notNullish)
})

defineOptions({
  name: 'TableOfContents',
})
</script>
